import { useEffect, useRef, useState } from "react";
import { DAY_MS, lastDayUnit, trialDaysLeft, trialMsLeft } from "../utils/trialCountdown";

/**
 * Live time left in a Kjuup.io trial.
 *
 * Ticks every second during the last day and every minute before that, and
 * recomputes as soon as the tab becomes visible again, so revisiting never
 * shows a stale value (browsers throttle timers in background tabs).
 * `onEnded` fires once per trialEnd when the countdown reaches zero.
 */
export default function useTrialCountdown({ trialEnd, clockOffsetMs = 0, enabled = true, onEnded }) {
  const [now, setNow] = useState(() => Date.now());
  const msLeft = enabled ? trialMsLeft(trialEnd, clockOffsetMs, now) : null;
  const running = msLeft != null && msLeft > 0;
  const lastDay = running && msLeft <= DAY_MS;

  useEffect(() => {
    setNow(Date.now());
  }, [trialEnd, clockOffsetMs]);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), lastDay ? 1000 : 60 * 1000);
    return () => clearInterval(id);
  }, [running, lastDay]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setNow(Date.now());
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const endedForRef = useRef(null);
  useEffect(() => {
    if (msLeft === 0 && trialEnd && endedForRef.current !== trialEnd) {
      endedForRef.current = trialEnd;
      if (onEndedRef.current) onEndedRef.current();
    }
  }, [msLeft, trialEnd]);

  return {
    msLeft,
    lastDay,
    ended: msLeft === 0,
    days: trialDaysLeft(msLeft),
    unit: lastDayUnit(msLeft),
  };
}
